"use client";

import { useEffect } from "react";
import { useTheme } from "@teispace/next-themes";

const STYLE_ID = "navigation-progress-bar-styles";
const DONE_DELAY_MS = 300;

/** Subset of the nprogress API we use (avoid `typeof import("…")` — rewrite tools keep stripping the module path). */
type NProgressInstance = {
  configure: (options: {
    showSpinner?: boolean;
    trickleSpeed?: number;
    minimum?: number;
    speed?: number;
    easing?: string;
  }) => unknown;
  start: () => unknown;
  done: (force?: boolean) => unknown;
};

let nprogressModule: NProgressInstance | null = null;
let patchInstalled = false;
let inFlight = 0;
let doneTimer: ReturnType<typeof setTimeout> | null = null;

function getHeader(
  headers: HeadersInit | undefined,
  name: string,
): string | null {
  if (!headers) return null;

  if (headers instanceof Headers) {
    return headers.get(name);
  }

  if (Array.isArray(headers)) {
    const entry = headers.find(
      ([key]) => key.toLowerCase() === name.toLowerCase(),
    );
    return entry?.[1] ?? null;
  }

  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === name.toLowerCase()) {
      return value;
    }
  }

  return null;
}

function resolveRequest(
  input: RequestInfo | URL,
  init?: RequestInit,
): { url: string; headers: HeadersInit | undefined } {
  if (input instanceof Request) {
    return { url: input.url, headers: input.headers };
  }

  const url = typeof input === "string" ? input : input.href;
  return { url, headers: init?.headers };
}

function normalizeParamsForProgressComparison(params: URLSearchParams): string {
  const normalized = new URLSearchParams(params);
  normalized.delete("template");
  normalized.delete("_rsc");
  return normalized.toString();
}

function isTemplateOnlyQueryUpdate(requestUrl: string): boolean {
  try {
    const current = new URL(window.location.href);
    const requested = new URL(requestUrl, window.location.href);

    if (current.origin !== requested.origin) return false;
    if (current.pathname !== requested.pathname) return false;

    return (
      normalizeParamsForProgressComparison(current.searchParams) ===
      normalizeParamsForProgressComparison(requested.searchParams)
    );
  } catch {
    return false;
  }
}

function shouldTrackFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean {
  const { url, headers } = resolveRequest(input, init);

  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) return false;
    if (isTemplateOnlyQueryUpdate(url)) return false;

    if (getHeader(headers, "next-router-prefetch") === "1") return false;
    if (getHeader(headers, "next-router-segment-prefetch")) return false;
    if (getHeader(headers, "next-hmr-refresh") === "1") return false;
    if (getHeader(headers, "purpose")?.toLowerCase() === "prefetch") {
      return false;
    }

    const rscHeader = getHeader(headers, "rsc") ?? getHeader(headers, "RSC");
    return rscHeader === "1";
  } catch {
    return false;
  }
}

function cancelScheduledDone() {
  if (doneTimer) {
    clearTimeout(doneTimer);
    doneTimer = null;
  }
}

function scheduleDone() {
  cancelScheduledDone();
  doneTimer = setTimeout(() => {
    doneTimer = null;
    if (inFlight === 0) {
      nprogressModule?.done();
    }
  }, DONE_DELAY_MS);
}

function installFetchPatch() {
  if (patchInstalled || typeof window === "undefined") return;
  patchInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    if (!shouldTrackFetch(input, init)) {
      return originalFetch(input, init);
    }

    inFlight += 1;
    if (inFlight === 1) {
      cancelScheduledDone();
      nprogressModule?.start();
    }

    try {
      return await originalFetch(input, init);
    } finally {
      inFlight -= 1;
      if (inFlight === 0) {
        scheduleDone();
      }
    }
  };
}

function updateProgressStyles(color: string) {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }

  style.textContent = `
    #nprogress { pointer-events: none; }
    #nprogress .bar {
      background: ${color};
      position: fixed;
      z-index: 1600;
      top: 0;
      left: 0;
      width: 100%;
      height: 2px;
    }
    #nprogress .peg {
      box-shadow: 0 0 8px ${color}66;
    }
  `;
}

export default function NavigationProgressBar() {
  const { resolvedTheme } = useTheme();
  const color = resolvedTheme === "dark" ? "#ffffff" : "#000000";

  useEffect(() => {
    let mounted = true;

    void import("nprogress").then((NProgress) => {
      if (!mounted) return;

      nprogressModule = NProgress;
      NProgress.configure({
        showSpinner: false,
        trickleSpeed: 200,
        minimum: 0.08,
        speed: 200,
        easing: "ease",
      });

      installFetchPatch();
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    updateProgressStyles(color);
  }, [color]);

  return null;
}
